import {createApi} from "@reduxjs/toolkit/query/react"
import {IBasicResponse} from "../../types/api"
import baseQuery from "../../utils/apiIIKOConfig"
import {
    IPermissionListResponse,
    IUserByIdParams,
    IUserCreatePayload,
    IUserCreateResponse,
    IUserDetails,
    IUserListItem,
    IUserSelf,
    IUserUpdateBranchListPayload,
    IUserUpdatePasswordPayload,
    IUserUpdatePayload
} from "./UserApiTypes"

const USER_API_TIME_ZONE = "Asia/Tashkent"

export const userApi = createApi({
    reducerPath: "userApi",
    baseQuery,
    tagTypes: ["users", "user-self", "permissions"],
    endpoints: (build) => ({
        getUsers: build.query<IBasicResponse<IUserListItem[]>, {lang: string}>({
            query: ({lang}) => ({
                url: "/v1/users",
                method: "GET",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": USER_API_TIME_ZONE
                }
            }),
            providesTags: ["users"]
        }),
        getUserById: build.query<IBasicResponse<IUserDetails>, IUserByIdParams>({
            query: ({lang, id}) => ({
                url: `/v1/users/${id}`,
                method: "GET",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": USER_API_TIME_ZONE
                }
            }),
            providesTags: (_result, _error, arg) => [{type: "users", id: arg.id}]
        }),
        getSelfUser: build.query<IBasicResponse<IUserSelf>, {lang: string}>({
            query: ({lang}) => ({
                url: "/v1/users/self",
                method: "GET",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": USER_API_TIME_ZONE
                }
            }),
            providesTags: ["user-self"]
        }),
        getPermissionList: build.query<IBasicResponse<IPermissionListResponse>, {lang: string}>({
            query: ({lang}) => ({
                url: "/v1/permissions",
                method: "GET",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": USER_API_TIME_ZONE
                }
            }),
            providesTags: ["permissions"]
        }),
        createUser: build.mutation<IBasicResponse<IUserCreateResponse>, IUserCreatePayload>({
            query: ({lang, ...body}) => ({
                url: "/v1/users",
                method: "POST",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": USER_API_TIME_ZONE,
                },
                body
            }),
            invalidatesTags: ["users", "user-self"]
        }),
        updateUser: build.mutation<IBasicResponse<IUserDetails>, IUserUpdatePayload>({
            query: ({lang, id, ...body}) => ({
                url: `/v1/users/${id}`,
                method: "PUT",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": USER_API_TIME_ZONE,
                },
                body
            }),
            invalidatesTags: (_result, _error, arg) => ["users", "user-self", {type: "users", id: arg.id}]
        }),
        updateUserPassword: build.mutation<IBasicResponse<void>, IUserUpdatePasswordPayload>({
            query: ({lang, id, password}) => ({
                url: `/v1/users/${id}/passwords`,
                method: "PATCH",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": USER_API_TIME_ZONE,
                },
                body: {password}
            })
        }),
        updateUserBranchList: build.mutation<IBasicResponse<void>, IUserUpdateBranchListPayload>({
            query: ({lang, id, branch_id_list}) => ({
                url: `/v1/users/${id}/branches`,
                method: "PATCH",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": USER_API_TIME_ZONE,
                },
                body: {branch_id_list}
            }),
            invalidatesTags: (_result, _error, arg) => ["users", "user-self", {type: "users", id: arg.id}]
        }),
        deleteUser: build.mutation<IBasicResponse<void>, IUserByIdParams>({
            query: ({lang, id}) => ({
                url: `/v1/users/${id}`,
                method: "DELETE",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": USER_API_TIME_ZONE
                }
            }),
            invalidatesTags: (_result, _error, arg) => ["users", "user-self", {type: "users", id: arg.id}]
        })
    })
})

export const {
    useGetUsersQuery,
    useGetUserByIdQuery,
    useGetSelfUserQuery,
    useGetPermissionListQuery,
    useCreateUserMutation,
    useUpdateUserMutation,
    useUpdateUserPasswordMutation,
    useUpdateUserBranchListMutation,
    useDeleteUserMutation
} = userApi
